import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { TOKENLOCKER_ABI, ERC20_ABI, CONTRACT_ADDRESSES, KATANA_NETWORK } from '../utils/contracts';
import { TokenLock, LockFormData } from '../types/contract';

export interface Web3State {
  account: string | null;
  provider: ethers.BrowserProvider | null;
  signer: ethers.Signer | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
}

export const useWeb3 = () => {
  const [state, setState] = useState<Web3State>({
    account: null,
    provider: null,
    signer: null,
    isConnected: false,
    isConnecting: false,
    error: null
  });

  const ensureKatana = useCallback(async (provider: ethers.BrowserProvider) => {
    const currentChainIdHex = (await provider.send('eth_chainId', [])) as string;
    if (currentChainIdHex?.toLowerCase() !== KATANA_NETWORK.chainIdHex.toLowerCase()) {
      try {
        await window.ethereum?.request?.({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: KATANA_NETWORK.chainIdHex }]
        });
      } catch (switchError: any) {
        if (switchError?.code === 4902) {
          await window.ethereum?.request?.({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: KATANA_NETWORK.chainIdHex,
                chainName: KATANA_NETWORK.name,
                nativeCurrency: { name: KATANA_NETWORK.currency, symbol: KATANA_NETWORK.currency, decimals: 18 },
                rpcUrls: [KATANA_NETWORK.rpcUrl]
              }
            ]
          });
        } else {
          throw switchError;
        }
      }
    }
  }, []);

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      setState(prev => ({ ...prev, error: 'MetaMask is not installed' }));
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      const provider = new ethers.BrowserProvider(window.ethereum!);
      await ensureKatana(provider);
      await provider.send('eth_requestAccounts', []);
      const signer = await provider.getSigner();
      const account = await signer.getAddress();

      setState({
        account,
        provider,
        signer,
        isConnected: true,
        isConnecting: false,
        error: null
      });
    } catch (error) {
      console.error('Error connecting wallet:', error);
      setState(prev => ({
        ...prev,
        isConnecting: false,
        error: error instanceof Error ? error.message : 'Failed to connect wallet'
      }));
    }
  }, []);

  const disconnectWallet = useCallback(() => {
    setState({
      account: null,
      provider: null,
      signer: null,
      isConnected: false,
      isConnecting: false,
      error: null
    });
  }, []);

  // Auto-connect on mount if previously connected
  useEffect(() => {
    const checkConnection = async () => {
      if (window.ethereum) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum!);
          await ensureKatana(provider);
          const accounts = await provider.listAccounts();
          
          if (accounts.length > 0) {
            const signer = await provider.getSigner();
            const account = await signer.getAddress();
            
            setState({
              account,
              provider,
              signer,
              isConnected: true,
              isConnecting: false,
              error: null
            });
          }
        } catch (error) {
          console.error('Error checking wallet connection:', error);
        }
      }
    };

    checkConnection();
  }, []);

  // Listen for account changes
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else {
          setState(prev => ({ ...prev, account: accounts[0] }));
        }
      };

      const handleChainChanged = () => {
        window.location.reload();
      };

      window.ethereum?.on?.('accountsChanged', handleAccountsChanged);
      window.ethereum?.on?.('chainChanged', handleChainChanged);

      return () => {
        window.ethereum?.removeListener?.('accountsChanged', handleAccountsChanged);
        window.ethereum?.removeListener?.('chainChanged', handleChainChanged);
      };
    }
  }, [disconnectWallet]);

  return {
    ...state,
    connectWallet,
    disconnectWallet
  };
};

export const useTokenLocker = (overrides?: { signer?: ethers.Signer | null; isConnected?: boolean }) => {
  const web3 = useWeb3();
  const signer = overrides?.signer ?? web3.signer;
  const isConnected = overrides?.isConnected ?? web3.isConnected;

  const getContract = useCallback(() => {
    if (!signer || !isConnected) return null;
    return new ethers.Contract(CONTRACT_ADDRESSES.TOKENLOCKER, TOKENLOCKER_ABI, signer);
  }, [signer, isConnected]);

  const lockTokens = useCallback(async (data: LockFormData) => {
    const contract = getContract();
    if (!contract) throw new Error('Wallet not connected');

    const amount = ethers.parseUnits(data.amount, 18); // Assuming 18 decimals
    const unlockDate = BigInt(Math.floor(new Date(data.unlockDate).getTime() / 1000));
    const lockFee = await contract.lockFee();

    const tx = await contract.lockTokens(data.tokenAddress, amount, unlockDate, { value: lockFee });
    return await tx.wait();
  }, [getContract]);

  const withdraw = useCallback(async (lockId: bigint) => {
    const contract = getContract();
    if (!contract) throw new Error('Wallet not connected');

    const tx = await contract.withdraw(lockId);
    return await tx.wait();
  }, [getContract]);

  const transferLock = useCallback(async (lockId: bigint, newOwner: string) => {
    const contract = getContract();
    if (!contract) throw new Error('Wallet not connected');

    const tx = await contract.transferLock(lockId, newOwner);
    return await tx.wait();
  }, [getContract]);

  const getUserLocks = useCallback(async (user: string) => {
    const contract = getContract();
    if (!contract) return [];

    const lockIds = await contract.getUserLocks(user);
    const locks: TokenLock[] = [];

    for (const lockId of lockIds) {
      const lock = await contract.getLock(lockId);
      locks.push(lock);
    }

    return locks;
  }, [getContract]);

  const getActiveLocks = useCallback(async (user: string) => {
    const contract = getContract();
    if (!contract) return [];

    return await contract.getActiveLocks(user);
  }, [getContract]);

  const getLockFee = useCallback(async () => {
    const contract = getContract();
    if (!contract) return 0n;

    return await contract.getLockFee();
  }, [getContract]);

  return {
    lockTokens,
    withdraw,
    transferLock,
    getUserLocks,
    getActiveLocks,
    getLockFee
  };
};

export const useERC20 = (tokenAddress: string) => {
  const { signer, isConnected } = useWeb3();

  const getContract = useCallback(() => {
    if (!signer || !isConnected) return null;
    return new ethers.Contract(tokenAddress, ERC20_ABI, signer);
  }, [signer, isConnected, tokenAddress]);

  const getTokenInfo = useCallback(async () => {
    const contract = getContract();
    if (!contract) return null;

    const [name, symbol, decimals] = await Promise.all([
      contract.name(),
      contract.symbol(),
      contract.decimals()
    ]);

    return { name, symbol, decimals: Number(decimals) };
  }, [getContract]);

  const getBalance = useCallback(async (account: string) => {
    const contract = getContract();
    if (!contract) return 0n;

    return await contract.balanceOf(account);
  }, [getContract]);

  const getAllowance = useCallback(async (owner: string, spender: string) => {
    const contract = getContract();
    if (!contract) return 0n;

    return await contract.allowance(owner, spender);
  }, [getContract]);

  const approve = useCallback(async (spender: string, amount: bigint) => {
    const contract = getContract();
    if (!contract) throw new Error('Wallet not connected');

    const tx = await contract.approve(spender, amount);
    return await tx.wait();
  }, [getContract]);

  return {
    getTokenInfo,
    getBalance,
    getAllowance,
    approve
  };
};
